import type { CSSProperties, ReactElement } from 'react';

// Phase 5.X-docx-to-pdf-service —
// Visual badge for the DOCX → PDF conversion state. Renders only when
// the document came from a DOCX upload (`status` is non-null); native
// PDFs and other non-converted docs render `null` from the parent so
// the badge doesn't appear at all.
//
// Three states:
//   - pending : blue spinner — conversion in flight
//   - success : green check  — PDF available; doc usable in eCTD compile
//   - failed  : red badge    — operator can retry via the "Retry" button
//
// Anything else (a stray value, future "queued" state, etc.) falls
// through to a neutral grey label so the UI doesn't crash.

export type ConversionStatusValue = 'pending' | 'success' | 'failed' | string | null | undefined;

export interface ConversionStatusBadgeProps {
  readonly status: ConversionStatusValue;
  readonly style?: CSSProperties;
}

const baseBadgeStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '1px 6px',
  borderRadius: 4,
  fontSize: 10,
  fontWeight: 600,
  lineHeight: 1.4,
  textTransform: 'uppercase',
  letterSpacing: 0.3,
  whiteSpace: 'nowrap',
};

const pendingStyle: CSSProperties = {
  ...baseBadgeStyle,
  background: '#dbeafe',
  color: '#1d4ed8',
  border: '1px solid #93c5fd',
};

const successStyle: CSSProperties = {
  ...baseBadgeStyle,
  background: '#dcfce7',
  color: '#15803d',
  border: '1px solid #86efac',
};

const failedStyle: CSSProperties = {
  ...baseBadgeStyle,
  background: '#fee2e2',
  color: '#b91c1c',
  border: '1px solid #fca5a5',
};

const unknownStyle: CSSProperties = {
  ...baseBadgeStyle,
  background: '#f3f4f6',
  color: '#6b7280',
  border: '1px solid #d1d5db',
};

export function ConversionStatusBadge({
  status,
  style,
}: ConversionStatusBadgeProps): ReactElement | null {
  if (status === null || status === undefined) return null;
  if (status === 'pending') {
    return (
      <span
        data-testid="conversion-status-badge-pending"
        title="DOCX → PDF conversion in progress"
        style={{ ...pendingStyle, ...(style ?? {}) }}
      >
        ⏳ Converting
      </span>
    );
  }
  if (status === 'success') {
    return (
      <span
        data-testid="conversion-status-badge-success"
        title="DOCX successfully converted to PDF"
        style={{ ...successStyle, ...(style ?? {}) }}
      >
        ✅ Converted
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span
        data-testid="conversion-status-badge-failed"
        title="DOCX → PDF conversion failed; retry via the inspector"
        style={{ ...failedStyle, ...(style ?? {}) }}
      >
        ❌ Failed
      </span>
    );
  }
  return (
    <span
      data-testid="conversion-status-badge-unknown"
      title={`Unknown conversion status: ${status}`}
      style={{ ...unknownStyle, ...(style ?? {}) }}
    >
      {String(status)}
    </span>
  );
}
